import { useState, useEffect } from "react";
import {
  processUnlocodeFile,
  processImoFile,
  processSubregionFile,
  downloadFacilityCodes,
  downloadGlnTable,
} from "../model/GLNModel";
import unlocodeFile from "../../assets/unlocode.xlsx";
import imoFile from "../../assets/IMO_sample.xlsx";
import subregionFile from "../../assets/subregion_sample.xlsx";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";

const GLNUploadVM = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadType, setUploadType] = useState("SUBREGION");
  const [selectedCountry, setSelectedCountry] = useState("All");
  const [hasUploadedSubregion, setHasUploadedSubregion] = useState(false);
  const { handleToastOpen } = useToast();

  // Check local storage on component mount to see if SUBREGION has been uploaded
  useEffect(() => {
    const subregionUploaded = localStorage.getItem("subregionUploaded");
    if (subregionUploaded === "true") {
      setHasUploadedSubregion(true);
    }
  }, []);

  const handleFileChoose = (files) => {
    const file = files[0];
    setSelectedFile(file);
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      handleToastOpen("error", "Please select a file first");
      return;
    }

    // If trying to upload UNLOCODE but SUBREGION hasn't been uploaded
    if (uploadType === "UNLOCODE" && !hasUploadedSubregion) {
      handleToastOpen(
        "error",
        "You must upload SUBREGION data first before uploading UNLOCODE files"
      );
      return;
    }

    setIsLoading(true);
    try {
      console.log("Selected File Details:", {
        name: selectedFile.name,
        type: selectedFile.type,
        size: selectedFile.size,
        uploadType: uploadType,
      });

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("fileType", uploadType);

      let response;
      if (uploadType === "UNLOCODE") {
        response = await processUnlocodeFile(formData);

        handleToastOpen(
          "success",
          `UNLOCODE file processed successfully: ${
            response.newRecordsProcessed
          } new records, ${response.recordsUpdated} updated records, ${
            response.recordsOverwritten || 0
          } overwritten records`
        );
      } else if (uploadType === "IMO") {
        response = await processImoFile(formData);

        if (response && response.stats) {
          const stats = response.stats;

          handleToastOpen(
            "success",
            `IMO file processed successfully: ${
              stats.facility_table.inserted_records
            } new records, ${
              stats.facility_table.updated_records
            } updated records, ${stats.errors || 0} errors`
          );
        } else {
          handleToastOpen("success", `IMO file processed successfully`);
        }
      } else if (uploadType === "SUBREGION") {
        response = await processSubregionFile(formData);

        console.log("response", response);
        if (response && response.stats) {
          const stats = response.stats;

          // Check if any records were processed successfully
          const totalRecordsProcessed =
            stats.region_table.new_records + stats.region_table.updated_records;

          if (totalRecordsProcessed > 0) {
            // Set the flag that SUBREGION has been uploaded
            setHasUploadedSubregion(true);
            localStorage.setItem("subregionUploaded", "true");
          }

          handleToastOpen(
            "success",
            `Subregion file processed successfully: ${
              stats.region_table.new_records
            } new records, ${
              stats.region_table.updated_records
            } updated records, ${stats.error_count || 0} errors`
          );
        } else {
          handleToastOpen("success", `Subregion file processed successfully`);
          // Since we don't have stats, assume success and set the flag
          setHasUploadedSubregion(true);
          localStorage.setItem("subregionUploaded", "true");
        }
      }

      setSelectedFile(null);
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        if (errorData.message && errorData.message.includes("duplicate")) {
          handleToastOpen("error", "Duplicate records found in the file");
        } else if (errorData.message) {
          handleToastOpen("error", errorData.message);
        } else {
          handleToastOpen("error", `Error processing ${uploadType} file`);
        }
      } else {
        handleToastOpen(
          "error",
          error.message || `Error processing ${uploadType} file`
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSampleFileDownload = () => {
    const link = document.createElement("a");

    if (uploadType === "UNLOCODE") {
      link.href = unlocodeFile;
      link.download = "unlocode_sample.xlsx";
    } else if (uploadType === "IMO") {
      link.href = imoFile;
      link.download = "IMO_sample.xlsx";
    } else if (uploadType === "SUBREGION") {
      link.href = subregionFile;
      link.download = "subregion_sample.xlsx";
    }

    link.click();
  };

  const handleBicSmdgDownload = async () => {
    setIsLoading(true);
    try {
      console.log(`Exporting facility codes for country: ${selectedCountry}`);
      await downloadFacilityCodes(selectedCountry);

      // More user-friendly success message
      const message =
        selectedCountry === "All"
          ? "Facility codes for all countries exported successfully"
          : `Facility codes for ${selectedCountry} exported successfully`;

      handleToastOpen("success", message);
    } catch (error) {
      if (error.message && error.message.includes("timeout")) {
        handleToastOpen(
          "error",
          "The request took too long to complete. Please try again later."
        );
      } else {
        handleToastOpen(
          "error",
          "Unable to export facility codes. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // For GLN Table Export
  const handleGlnTableDownload = async () => {
    setIsLoading(true);
    try {
      console.log(`Exporting GLN table for country: ${selectedCountry}`);
      await downloadGlnTable(selectedCountry);

      // More user-friendly success message
      const message =
        selectedCountry === "All"
          ? "GLN table data for all countries exported successfully"
          : `GLN table data for ${selectedCountry} exported successfully`;

      handleToastOpen("success", message);
    } catch (error) {
      if (error.message && error.message.includes("timeout")) {
        handleToastOpen(
          "error",
          "The request took too long to complete. Please try again later."
        );
      } else {
        handleToastOpen(
          "error",
          "Unable to export GLN table data. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadTypeChange = (value) => {
    // If trying to select UNLOCODE but SUBREGION hasn't been uploaded
    if (value === "UNLOCODE" && !hasUploadedSubregion) {
      handleToastOpen(
        "warning",
        "You must upload SUBREGION data first before uploading UNLOCODE files"
      );
      // Still allow them to select it, but they'll get another warning when they try to upload
    }

    setUploadType(value);
    setSelectedFile(null);
  };

  const handleCountryChange = (value) => {
    console.log(`Country selection changed to: ${value}`);
    setSelectedCountry(value);
  };

  const hasBtnPermission = () => {
    const permissionData = localStorage.getItem("permissionData");
    const newData = "feature_gln_download";
    if (permissionData && permissionData.includes(newData)) {
      return true;
    }
    return false;
  };

  return {
    selectedFile,
    isLoading,
    uploadType,
    selectedCountry,
    handleFileChoose,
    handleUploadFile,
    hasBtnPermission,
    handleSampleFileDownload,
    handleBicSmdgDownload,
    handleGlnTableDownload,
    handleUploadTypeChange,
    handleCountryChange,
  };
};

export default GLNUploadVM;
